import React from 'react';
import ProjectInfo from '../projects/ProjectInfo';
import ProjectForm from '../projects/ProjectForm';
import ItemList from '../items/ItemList';
import CableList from '../cables/CableList';
import SwitchboardsPanel from '../lookups/SwitchboardsPanel';
import LookupPanel from '../lookups/LookupPanel';
import UsersPanel from '../users/UsersPanel';
import FuseList from '../reports/FuseList';
import LoadCalculation from '../reports/LoadCalculation';
import SingleLineDiagram from '../reports/SingleLineDiagram';
import MeggerLists from '../reports/MeggerLists';
import QuoteEditor from '../quotes/QuoteEditor';
import QuoteSummary from '../quotes/QuoteSummary';
import EmployeeOverview from '../users/EmployeeOverview';
import Settings from '../settings/Settings';
import FileManager from '../files/FileManager';
import PersonalFileManager from '../files/PersonalFileManager';
import PdfViewer from '../files/PdfViewer';
import GeneratedPdfViewer from '../files/GeneratedPdfViewer';
import TextViewer from '../files/TextViewer';
import ChatWindow from '../chat/ChatWindow';
import GanttView from '../planning/GanttView';
import Calendar from '../calendar/Calendar';
import JobPackageView from '../jobpackages/JobPackageView';
import FeedbackPanel from '../feedback/FeedbackPanel';
import './EditorArea.css';

function EditorArea({ activeTab, tabs, activeProject, onOpenTab, onCloseTab, setTabs, role, onProjectSaved, onUserUpdate, socket, currentUser, projects, onlineUsers, onJobPkgRead, onFeedbackRead }) {
  const renderTab = (tab) => {
    const proj = tab.project ?? activeProject;
    const type = tab.type ?? tab.id;

    if (type === 'project-info') {
      return <ProjectInfo project={proj} onOpenTab={onOpenTab} onProjectSaved={onProjectSaved} role={role} />;
    }
    if (tab.id === 'project-new') {
      return (
        <ProjectForm project={null}
          onSaved={(p) => { setTabs(prev => prev.filter(t => t.id !== 'project-new')); onProjectSaved(p); onOpenTab(`project-info-${p._id}`, p.name, { type: 'project-info', project: p }); }}
          onCancel={() => onCloseTab('project-new')} />
      );
    }
    if (type === 'project-edit' && proj) {
      return (
        <ProjectForm project={proj}
          onSaved={(p) => { setTabs(prev => prev.filter(t => t.id !== tab.id)); onProjectSaved(p); onOpenTab(`project-info-${p._id}`, p.name, { type: 'project-info', project: p }); }}
          onCancel={() => onCloseTab(tab.id)} />
      );
    }
    if (type === 'load-list') {
      return <ItemList project={proj} isActive={activeTab === tab.id} role={role} highlightTag={tab.highlightTag} onOpenTab={onOpenTab} />;
    }
    if (type === 'cable-register') {
      return <CableList project={proj} isActive={activeTab === tab.id} role={role} highlightTag={tab.highlightTag} onOpenTab={onOpenTab} />;
    }
    if (type === 'switchboards') return <SwitchboardsPanel project={proj} role={role} onOpenTab={onOpenTab} projects={projects} isActive={activeTab === tab.id} />;
    if (type === 'rooms')        return <LookupPanel project={proj} type="rooms"      role={role} projects={projects} isActive={activeTab === tab.id} />;
    if (type === 'systems')      return <LookupPanel project={proj} type="systems"    role={role} projects={projects} isActive={activeTab === tab.id} />;
    if (type === 'sections')     return <LookupPanel project={proj} type="sections"   role={role} projects={projects} isActive={activeTab === tab.id} />;
    if (type === 'cable-types')  return <LookupPanel project={proj} type="cableTypes" role={role} projects={projects} isActive={activeTab === tab.id} />;
    if (type === 'emg-groups')   return <LookupPanel project={proj} type="emgGroups"  role={role} projects={projects} isActive={activeTab === tab.id} />;
    if (type === 'fuse-list')    return <FuseList    project={proj} role={role} onOpenTab={onOpenTab} />;
    if (type === 'megger-lists') return <MeggerLists project={proj} role={role} onOpenTab={onOpenTab} />;
    if (type === 'load-calc')    return <LoadCalculation project={proj} role={role} onOpenTab={onOpenTab} />;
    if (type === 'sld')          return <SingleLineDiagram project={proj} role={role} onOpenTab={onOpenTab} />;
    if (type === 'quote-summary') return <QuoteSummary project={proj} onOpenTab={onOpenTab} />;
    if (type === 'quote') return (
      <QuoteEditor quoteId={tab.quoteId} tabId={tab.id} project={proj} isActive={activeTab === tab.id} onOpenTab={onOpenTab}
        onRename={label => setTabs(prev => prev.map(t => (t.id === tab.id ? { ...t, label } : t)))} />
    );
    if (tab.id === 'users')          return <UsersPanel role={role} />;
    if (tab.id === 'employee-overview') return <EmployeeOverview projects={projects} />;
    if (tab.id === 'feedback' || type === 'feedback') return <FeedbackPanel onCountChange={onFeedbackRead} />;
    if (type === 'gantt-diagram')    return <GanttView diagram={tab.diagram} project={proj} role={role} onOpenTab={onOpenTab} />;
    if (type === 'project-calendar') return <Calendar mode="project" project={proj} role={role} socket={socket} />;
    if (tab.id === 'my-calendar')    return <Calendar mode="my" projects={projects} role={role} socket={socket} />;
    if (type === 'print-pdf')        return <GeneratedPdfViewer url={tab.pdfUrl} title={tab.label} />;
    if (type === 'job-packages')    return <JobPackageView project={proj} role={role} currentUser={currentUser} onJobPkgRead={onJobPkgRead} highlightPkg={tab.highlightPkg} onOpenTab={onOpenTab} socket={socket} />;
    if (type === 'my-job-packages') return <JobPackageView project={null} role={role} currentUser={currentUser} onJobPkgRead={onJobPkgRead} highlightPkg={tab.highlightPkg} onOpenTab={onOpenTab} socket={socket} />;
    if (tab.id === 'settings' || type === 'settings') return <Settings currentUser={currentUser} role={role} onUserUpdate={onUserUpdate} />;
    if (type === 'files-folder') {
      return <FileManager folder={tab.folder} project={proj} isActive={activeTab === tab.id} onOpenTab={onOpenTab} role={role} />;
    }
    if (type === 'personal-folder') {
      return <PersonalFileManager folder={tab.folder} isActive={activeTab === tab.id} onOpenTab={onOpenTab} role={role} />;
    }
    if (type === 'pdf-viewer')  return <PdfViewer  file={tab.file} />;
    if (type === 'txt-viewer')  return <TextViewer file={tab.file} />;
    if (type === 'chat-window') {
      return (
        <ChatWindow
          group={tab.group}
          isActive={activeTab === tab.id}
          socket={socket}
          currentUser={currentUser}
          projects={projects}
          onOpenTab={onOpenTab}
          role={role}
          onlineUsers={onlineUsers}
        />
      );
    }
    return <div className="editor-placeholder">Unknown tab</div>;
  };

  return (
    <div className="editor-area">
      {tabs.map((tab) => (
        <div key={tab.id} className={`editor-pane${activeTab === tab.id ? ' active' : ''}`}>
          {renderTab(tab)}
        </div>
      ))}
      {tabs.length === 0 && (
        <div className="editor-placeholder">
          <span>Select a section from the left panel</span>
        </div>
      )}
    </div>
  );
}

export default EditorArea;
